/**
 * Geofence Status Badge
 *
 * Shows whether user is inside or outside the room's geofence.
 * Includes special handling for LoRa extended boundaries.
 */

import { clsx } from 'clsx'
import type { GeofenceStatus as GeofenceStatusType } from '@/lib/types'
import { LORA_EXTENDED_BOUNDARY_METERS } from '@/lib/types'
import { formatDistance } from '@/lib/geo'

interface GeofenceStatusProps {
  status: GeofenceStatusType | null
  /** Whether we have location data */
  hasLocation?: boolean
  /** Whether we have room metadata (center/radius) */
  hasRoomData?: boolean
  /** Whether LoRa is connected (shows extended boundary info) */
  isLoRaConnected?: boolean
  className?: string
}

export function GeofenceStatus({
  status,
  hasLocation = false,
  hasRoomData = false,
  isLoRaConnected = false,
  className,
}: GeofenceStatusProps) {
  if (!status) {
    // Show different messages based on what we're waiting for
    let waitingMessage = 'TRIANGULATING...'
    if (!hasLocation && !hasRoomData) {
      waitingMessage = 'INITIALIZING...'
    } else if (!hasRoomData) {
      waitingMessage = 'SYNCING ROOM...'
    } else if (!hasLocation) {
      waitingMessage = 'GETTING GPS...'
    }

    return (
      <span
        className={clsx(
          'text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase tracking-wider border',
          'bg-white/5 text-white/40 border-white/10 animate-pulse',
          className
        )}
      >
        [ {waitingMessage} ]
      </span>
    )
  }

  // Check if using LoRa extended boundary
  if (status.isLoRaExtended) {
    // User is outside standard boundary but inside LoRa extended boundary
    return (
      <span
        className={clsx(
          'text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase tracking-wider border',
          'bg-purple-500/10 text-purple-400 border-purple-500/30',
          className
        )}
        title={`Inside LoRa extended boundary (${formatDistance(status.effectiveRadius ?? LORA_EXTENDED_BOUNDARY_METERS)}). You are ${formatDistance(status.distanceToCenter)} from room center.`}
      >
        [ 📻 LoRa RANGE ] {formatDistance(status.distanceToCenter)}
      </span>
    )
  }

  if (status.isInside) {
    return (
      <span
        className={clsx(
          'text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase tracking-wider border',
          'bg-green-500/10 text-green-400 border-green-500/30',
          className
        )}
      >
        [ GEOFENCE: INSIDE ] {formatDistance(status.distanceToCenter)}
      </span>
    )
  }

  // Outside both standard and extended boundary
  return (
    <span
      className={clsx(
        'text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase tracking-wider border',
        'bg-red-500/10 text-red-400 border-red-500/30',
        className
      )}
      title={isLoRaConnected
        ? `Outside even LoRa extended boundary (${formatDistance(status.effectiveRadius ?? LORA_EXTENDED_BOUNDARY_METERS)})`
        : undefined
      }
    >
      [ GEOFENCE: OUTSIDE ] +{formatDistance(status.distanceBeyondRadius ?? 0)}
    </span>
  )
}

/**
 * Geofence indicator dot for participant list
 */
interface GeofenceDotProps {
  isInside: boolean
  className?: string
}

export function GeofenceDot({ isInside, className }: GeofenceDotProps) {
  return (
    <span
      className={clsx(
        'w-2 h-2 rounded-full',
        isInside ? 'bg-green-500' : 'bg-red-500',
        className
      )}
      title={isInside ? 'Inside geofence' : 'Outside geofence'}
    />
  )
}
